import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Tag, 
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker,
  message, 
  Space, 
  Typography, 
  Row, 
  Col, 
  Statistic,
  Badge,
  Dropdown,
  Menu,
  Divider,
  Tabs,
  Table,
  Tooltip,
  Popconfirm
} from 'antd';
import { 
  EyeOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined,
  UserOutlined,
  SearchOutlined,
  DownloadOutlined,
  MoreOutlined,
  ReloadOutlined,
  FileTextOutlined,
  CalendarOutlined,
  DollarOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  FilterOutlined,
  ExportOutlined
} from '@ant-design/icons';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc, 
  deleteDoc,
  where,
  limit
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { theme } from '../../../styles/theme';
import { trackAdminEvent, ANALYTICS_EVENTS } from '../../../utils/analytics';
import { SERVICE_FREQUENCIES, QUOTE_STATUSES, KANBAN_COLUMNS } from '../../../constants/services';
import { formatDate, formatCurrency } from '../../../utils/helpers';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const QuotesManagement = () => {
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    newInquiry: 0,
    quoteSent: 0,
    approved: 0,
    completed: 0,
    cancelled: 0,
    totalRevenue: 0
  });

  // Fetch quotes from Firestore
  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'quotes'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const quotesData = [];
      let totalRevenue = 0;
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const quoteData = {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        };
        quotesData.push(quoteData);
        
        // Calculate revenue for approved/completed quotes
        if ((data.status === 'approved' || data.status === 'completed') && data.quoteAmount) {
          totalRevenue += parseFloat(data.quoteAmount) || 0;
        }
      });
      
      setQuotes(quotesData);
      setFilteredQuotes(quotesData);
      
      // Calculate stats
      const newStats = {
        total: quotesData.length,
        newInquiry: quotesData.filter(q => q.status === 'new_inquiry').length,
        quoteSent: quotesData.filter(q => q.status === 'quote_sent').length,
        approved: quotesData.filter(q => q.status === 'approved').length,
        completed: quotesData.filter(q => q.status === 'completed').length,
        cancelled: quotesData.filter(q => q.status === 'cancelled').length,
        totalRevenue
      };
      setStats(newStats);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filter quotes based on search, status, and date range
  useEffect(() => {
    let filtered = quotes;

    // Search filter
    if (searchText) {
      filtered = filtered.filter(quote => 
        quote.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        quote.phone?.includes(searchText) ||
        quote.address?.toLowerCase().includes(searchText.toLowerCase()) ||
        quote.id?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(quote => quote.status === statusFilter);
    }

    // Date range filter
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      filtered = filtered.filter(quote => {
        const quoteDate = quote.createdAt;
        return quoteDate >= startDate.startOf('day') && quoteDate <= endDate.endOf('day');
      });
    }

    setFilteredQuotes(filtered);
  }, [quotes, searchText, statusFilter, dateRange]);

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await updateDoc(doc(db, 'quotes', quoteId), {
        status: newStatus,
        updatedAt: new Date()
      });
      
      trackAdminEvent(ANALYTICS_EVENTS.QUOTE_STATUS_CHANGED, {
        quoteId,
        newStatus,
        previousStatus: quotes.find(q => q.id === quoteId)?.status
      });
      
      message.success('Quote status updated successfully');
    } catch (error) {
      console.error('Error updating quote status:', error);
      message.error('Failed to update quote status');
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    try {
      await deleteDoc(doc(db, 'quotes', quoteId));
      
      trackAdminEvent(ANALYTICS_EVENTS.QUOTE_DELETED, {
        quoteId
      });
      
      message.success('Quote deleted successfully');
    } catch (error) {
      console.error('Error deleting quote:', error);
      message.error('Failed to delete quote');
    }
  };

  const handleEditQuote = async (values) => {
    try {
      await updateDoc(doc(db, 'quotes', selectedQuote.id), {
        ...values,
        updatedAt: new Date()
      });
      
      trackAdminEvent(ANALYTICS_EVENTS.QUOTE_UPDATED, {
        quoteId: selectedQuote.id,
        updatedFields: Object.keys(values)
      });
      
      message.success('Quote updated successfully');
      setEditModalVisible(false);
    } catch (error) {
      console.error('Error updating quote:', error);
      message.error('Failed to update quote');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new_inquiry': 'blue',
      'quote_sent': 'orange',
      'approved': 'green',
      'completed': 'purple',
      'cancelled': 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'new_inquiry': <ClockCircleOutlined />,
      'quote_sent': <EditOutlined />,
      'approved': <CheckCircleOutlined />,
      'completed': <CheckCircleOutlined />,
      'cancelled': <DeleteOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  const exportQuotes = () => {
    const csvContent = [
      ['ID', 'Email', 'Phone', 'Address', 'Status', 'Amount', 'Services', 'Created Date'],
      ...filteredQuotes.map(quote => [
        quote.id,
        quote.email || '',
        quote.phone || '',
        quote.address || '',
        QUOTE_STATUSES[quote.status] || quote.status,
        quote.quoteAmount || 0,
        quote.services?.join(', ') || '',
        formatDate(quote.createdAt)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quotes-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    message.success('Quotes exported successfully');
  };

  const columns = [
    {
      title: 'Quote ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (id) => (
        <Text 
          code 
          style={{ 
            fontSize: '12px',
            fontFamily: 'monospace',
            color: theme.colors.primary 
          }}
        >
          {id.substring(0, 8)}...
        </Text>
      )
    },
    {
      title: 'Customer',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email, record) => (
        <div>
          <div style={{ fontWeight: 'bold', color: theme.colors.black, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <MailOutlined style={{ fontSize: '12px' }} />
            {email}
          </div>
          {record.phone && (
            <div style={{ fontSize: '12px', color: theme.colors.gray, display: 'flex', alignItems: 'center', gap: '4px' }}>
              <PhoneOutlined style={{ fontSize: '10px' }} />
              {record.phone}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      width: 250,
      render: (address) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <HomeOutlined style={{ fontSize: '12px', color: theme.colors.gray }} />
          <Text ellipsis={{ tooltip: address, style: { maxWidth: '200px' } }}>
            {address}
          </Text>
        </div>
      )
    },
    {
      title: 'Services',
      dataIndex: 'services',
      key: 'services',
      width: 150,
      render: (services) => (
        <div>
          {services?.slice(0, 2).map((service, index) => (
            <Tag key={index} size="small" style={{ marginBottom: '2px' }}>
              {service}
            </Tag>
          ))}
          {services?.length > 2 && (
            <Tag size="small" color="blue">
              +{services.length - 2} more
            </Tag>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag 
          color={getStatusColor(status)} 
          icon={getStatusIcon(status)}
          style={{ fontFamily: theme.fonts.primary }}
        >
          {QUOTE_STATUSES[status] || status}
        </Tag>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'quoteAmount',
      key: 'quoteAmount',
      width: 100,
      render: (amount) => (
        <Text strong style={{ color: theme.colors.success, fontFamily: theme.fonts.primary }}>
          {formatCurrency(amount)}
        </Text>
      )
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => (
        <div>
          <div style={{ fontFamily: theme.fonts.primary, fontSize: '12px' }}>
            {formatDate(date)}
          </div>
          <div style={{ fontSize: '10px', color: theme.colors.gray }}>
            {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedQuote(record);
                setModalVisible(true);
              }}
              style={{ color: theme.colors.primary }}
            />
          </Tooltip>
          <Tooltip title="Edit Quote">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedQuote(record);
                editForm.setFieldsValue(record);
                setEditModalVisible(true);
              }}
              style={{ color: theme.colors.warning }}
            />
          </Tooltip>
          <Dropdown
            overlay={
              <Menu>
                {Object.entries(QUOTE_STATUSES).map(([key, label]) => (
                  <Menu.Item
                    key={key}
                    onClick={() => handleStatusChange(record.id, key)}
                    disabled={record.status === key}
                  >
                    {label}
                  </Menu.Item>
                ))}
                <Menu.Divider />
                <Menu.Item
                  key="delete"
                  onClick={() => handleDeleteQuote(record.id)}
                  style={{ color: theme.colors.error }}
                >
                  Delete Quote
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      )
    }
  ];

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex',
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <Title level={2} style={{ 
            margin: 0, 
            color: theme.colors.black, 
            fontFamily: theme.fonts.primary,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FileTextOutlined style={{ color: theme.colors.primary }} />
            Quote Management
          </Title>
          <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
            Manage and track all customer quotes
          </Text>
        </div>
        
        <Space>
          <Button 
            icon={<ExportOutlined />}
            onClick={exportQuotes}
            disabled={filteredQuotes.length === 0}
          >
            Export CSV
          </Button>
          <Button 
            icon={<ReloadOutlined />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Quotes"
              value={stats.total}
              valueStyle={{ color: theme.colors.primary }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="New Inquiries"
              value={stats.newInquiry}
              valueStyle={{ color: '#1890ff' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Approved"
              value={stats.approved}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={stats.totalRevenue}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: theme.colors.success }}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Input.Search
              placeholder="Search quotes by email, phone, address, or ID..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              suffixIcon={<FilterOutlined />}
            >
              <Option value="all">All Statuses</Option>
              {Object.entries(QUOTE_STATUSES).map(([key, label]) => (
                <Option key={key} value={key}>{label}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              placeholder={['Start Date', 'End Date']}
              value={dateRange}
              onChange={setDateRange}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text style={{ color: theme.colors.gray }}>
              Showing {filteredQuotes.length} of {quotes.length} quotes
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Quotes Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredQuotes}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} quotes`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* Quote Details Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileTextOutlined style={{ color: theme.colors.primary }} />
            Quote Details
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedQuote && (
          <div>
            <Tabs defaultActiveKey="details">
              <TabPane tab="Quote Details" key="details">
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                        Quote ID:
                      </Text>
                      <br />
                      <Text code style={{ color: theme.colors.gray, fontFamily: 'monospace' }}>
                        {selectedQuote.id}
                      </Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                        Status:
                      </Text>
                      <br />
                      <Tag 
                        color={getStatusColor(selectedQuote.status)} 
                        icon={getStatusIcon(selectedQuote.status)}
                        style={{ fontFamily: theme.fonts.primary }}
                      >
                        {QUOTE_STATUSES[selectedQuote.status] || selectedQuote.status}
                      </Tag>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                        Customer Email:
                      </Text>
                      <br />
                      <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                        {selectedQuote.email}
                      </Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                        Phone:
                      </Text>
                      <br />
                      <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                        {selectedQuote.phone || 'Not provided'}
                      </Text>
                    </div>
                  </Col>
                  <Col span={24}>
                    <div style={{ marginBottom: '16px' }}>
                      <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                        Address:
                      </Text>
                      <br />
                      <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                        {selectedQuote.address}
                      </Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                        Services:
                      </Text>
                      <br />
                      <div style={{ marginTop: '4px' }}>
                        {selectedQuote.services?.map((service, index) => (
                          <Tag key={index} style={{ marginBottom: '4px' }}>
                            {service}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                        Quote Amount:
                      </Text>
                      <br />
                      <Text strong style={{ color: theme.colors.success, fontFamily: theme.fonts.primary, fontSize: '16px' }}>
                        {formatCurrency(selectedQuote.quoteAmount)}
                      </Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                        Created:
                      </Text>
                      <br />
                      <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                        {formatDate(selectedQuote.createdAt)}
                      </Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                        Last Updated:
                      </Text>
                      <br />
                      <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                        {formatDate(selectedQuote.updatedAt)}
                      </Text>
                    </div>
                  </Col>
                </Row>
              </TabPane>
            </Tabs>
          </div>
        )}
      </Modal>

      {/* Edit Quote Modal */}
      <Modal
        title="Edit Quote"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditQuote}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: 'email' }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="phone"
            label="Phone"
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true }]}
          >
            <Select>
              {Object.entries(QUOTE_STATUSES).map(([key, label]) => (
                <Option key={key} value={key}>{label}</Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="quoteAmount"
            label="Quote Amount"
            rules={[{ required: true, type: 'number', min: 0 }]}
          >
            <Input type="number" step="0.01" prefix="$" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Update Quote
              </Button>
              <Button onClick={() => setEditModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QuotesManagement;
